import { issueReasonLabel, SageActionableError, UnitOfMeasureOption } from './sage-integration.model';

export type FixMatchType = 'Item' | 'Supplier';
/** The record buttons on Fix matches. Finished goods isn't a link list: it's where Bundle or Standalone is chosen. */
export type FixMatchesSection = FixMatchType | 'FinishedGood';
export const FIX_SECTIONS: FixMatchesSection[] = ['Item', 'Supplier', 'FinishedGood'];
export type FixMatchChip = 'all' | 'attention' | 'raw' | 'finished';

const RAW_MATERIAL_TYPE_ID = 1;
export const FINISHED_GOOD_TYPE_ID = 2;
const VARIABLE_ITEM_TYPE_ID = 3;

/** GET api/sage/links/{type}: one per Aivor record of the type, linked or not. */
export interface SageLinkRow {
  aivorRecordId: number;
  name: string | null;
  code: string | null;
  itemTypeId: number | null;
  sendsAs: 'Standalone' | 'Bundle' | null;
  isService: boolean;
  isActive: boolean;
  sageRecordId: number | null;
  canLink: boolean;
  cannotLinkReason: string | null;
  /** What Sage held before Aivor's first push. Null when Aivor made the Sage record itself. */
  originalSageName: string | null;
  originalSageCode: string | null;
  originalSageIsService: boolean | null;
  lastSyncedAt: string | null;
  stockQuantity: number | null;
  stockFromLink: number | null;
}

export interface SageLinkStockReversal {
  aivorRecordId: number;
  quantity: number;
  reversed: boolean;
}

export interface SageLinkMoveRequest {
  sageRecordId: number;
  aivorRecordId: number;
  swap: boolean;
}

export interface SageLinkMoveDetails {
  sageRecordId: number;
  linkedAivorRecordId: number;
  previousAivorRecordId: number;
  releasedSageRecordId: number | null;
  releasedAs: 'OldCopy' | 'Restored' | null;
  swapped: boolean;
  releasedSageQuantity: number | null;
  stockReversals: SageLinkStockReversal[];
}

/** A null code or name keeps the Sage record's own; a null unit is matched to Sage's on the server. */
export interface SageLinkAddRequest {
  sageRecordId: number;
  code: string | null;
  name: string | null;
  itemTypeId: number | null;
  uomId: number | null;
}

export interface SageLinkAddDetails {
  sageRecordId: number;
  newAivorRecordId: number;
  previousAivorRecordId: number;
  importedQuantity: number | null;
  stockReversals: SageLinkStockReversal[];
}

export type FixMatchChoice =
  | { kind: 'move'; target: SageLinkRow; swap: boolean }
  | { kind: 'new'; code: string; name: string; itemTypeId: number | null };

export interface FixMatchOption {
  row: SageLinkRow;
  disabledReason: string | null;
  linkedNote: string | null;
}

export interface FixMatchesView {
  section: FixMatchesSection;
  type: FixMatchType;
  rows: SageLinkRow[];
  allRows: SageLinkRow[];
  loading: boolean;
  loadError: boolean;
  search: string;
  chip: FixMatchChip;
  chipCounts: Record<FixMatchChip, number>;
  sectionCounts: Record<FixMatchesSection, number | null>;
  attention: ReadonlyMap<number, string>;
  fixed: ReadonlySet<number>;
  changing: SageLinkRow | null;
  units: UnitOfMeasureOption[];
  saving: boolean;
  error: string | null;
}

export const ADD_AS_NEW_TOAST = 'Added to Aivor. The next sync will use it.';
export const PICKER_LIMIT = 8;

export function fixMatchNoun(type: FixMatchType): string {
  return type === 'Item' ? 'item' : 'supplier';
}

export function fixChips(type: FixMatchType): { key: FixMatchChip; label: string }[] {
  const chips: { key: FixMatchChip; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'attention', label: 'Needs attention' },
  ];
  return type === 'Item'
    ? [...chips, { key: 'raw', label: 'Raw materials' }, { key: 'finished', label: 'Finished goods' }]
    : chips;
}

export function emptyLinksText(type: FixMatchType, search: string, chip: FixMatchChip): string {
  const nouns = type === 'Item' ? 'items' : 'suppliers';
  return search.trim() || chip !== 'all' ? `No linked ${nouns} match this filter.` : `No ${nouns} are linked to Sage yet.`;
}

export function linkedRows(rows: SageLinkRow[]): SageLinkRow[] {
  return rows.filter((r) => r.sageRecordId != null);
}

/** Linked records whose Sage record came from Sage; Aivor's own copies can't be changed. */
export function canChange(row: SageLinkRow): boolean {
  return row.originalSageName != null;
}

export function isFinishedGoodRow(row: SageLinkRow): boolean {
  return row.sendsAs != null;
}

const FIX_SECTION_LABELS: Record<FixMatchesSection, string> = {
  Item: 'Items',
  Supplier: 'Suppliers',
  FinishedGood: 'Finished goods',
};

/** "Items (42)"; no count until that section has loaded. */
export function fixSectionLabel(section: FixMatchesSection, count: number | null): string {
  const label = FIX_SECTION_LABELS[section];
  return count == null ? label : `${label} (${count})`;
}

/** As in Mockup 02, only Needs attention carries a count. */
export function fixChipLabel(chip: { key: FixMatchChip; label: string }, attentionCount: number): string {
  return chip.key === 'attention' ? `${chip.label} (${attentionCount})` : chip.label;
}

/** Mockup 02's type pills: teal for raw materials, navy for finished goods, grey for the rest. */
export function typePill(row: SageLinkRow): 'teal' | 'navy' | '' {
  if (row.isService || row.itemTypeId === VARIABLE_ITEM_TYPE_ID) return '';
  return isFinishedGoodRow(row) ? 'navy' : 'teal';
}

export function recordLine(row: SageLinkRow): string {
  const code = row.originalSageCode ? ` · ${row.originalSageCode}` : '';
  return `This is the record in Sage${code}. It is linked to ${row.name} now.`;
}

export function typeLabel(row: SageLinkRow): string {
  if (row.isService) return 'Service';
  if (row.itemTypeId === VARIABLE_ITEM_TYPE_ID) return 'Variable item';
  if (row.sendsAs === 'Bundle') return 'Finished good (bundle)';
  if (row.sendsAs) return 'Finished good';
  return 'Raw material';
}

function matchesChip(row: SageLinkRow, chip: FixMatchChip, attention: ReadonlyMap<number, string>): boolean {
  switch (chip) {
    case 'attention':
      return attention.has(row.aivorRecordId);
    case 'raw':
      return row.itemTypeId === RAW_MATERIAL_TYPE_ID && !isFinishedGoodRow(row);
    case 'finished':
      return isFinishedGoodRow(row);
    default:
      return true;
  }
}

function contains(value: string | null | undefined, query: string): boolean {
  return !!value && value.toLowerCase().includes(query);
}

export function filterLinkRows(
  rows: SageLinkRow[],
  search: string,
  chip: FixMatchChip,
  attention: ReadonlyMap<number, string>
): SageLinkRow[] {
  const query = search.trim().toLowerCase();
  return linkedRows(rows).filter(
    (r) =>
      matchesChip(r, chip, attention) &&
      (!query || [r.name, r.code, r.originalSageName, r.originalSageCode].some((v) => contains(v, query)))
  );
}

export function chipCount(rows: SageLinkRow[], chip: FixMatchChip, attention: ReadonlyMap<number, string>): number {
  return linkedRows(rows).filter((r) => matchesChip(r, chip, attention)).length;
}

// Only problems a wrong or broken link causes. Setup problems (categories, groups, units, bills of
// materials) stay under Errors, where they're fixed.
const LINK_ISSUES = new Set([
  'DuplicateName',
  'item_kind_mismatch',
  'ItemKindMismatch',
  'DuplicateSageLink',
  'AivorRecordAlreadyLinked',
  'NotFound',
]);

/** Finished goods' issues are logged by the finished goods sync, under ItemBundle. */
export function needsAttentionByRecord(issues: SageActionableError[], type: FixMatchType): Map<number, string> {
  const types = type === 'Item' ? ['Item', 'ItemBundle'] : ['Supplier'];
  const attention = new Map<number, string>();
  for (const issue of issues) {
    const row = issue.latest;
    if (row?.aivorRecordId == null || !types.includes(row.entityType) || attention.has(row.aivorRecordId)) continue;
    if (!LINK_ISSUES.has(row.errorCode ?? '')) continue;
    attention.set(row.aivorRecordId, issueReasonLabel(row.errorCode, row.category));
  }
  return attention;
}

function sameText(a: string | null | undefined, b: string | null | undefined): boolean {
  return !!a?.trim() && a.trim().toLowerCase() === (b ?? '').trim().toLowerCase();
}

// Mirrors the server's refusals, so a choice it would refuse can't be made.
function cannotPickReason(row: SageLinkRow, changing: SageLinkRow, type: FixMatchType): string | null {
  if (type === 'Supplier') {
    return sameText(row.name, changing.name) ? `Same name as ${changing.name}.` : null;
  }
  if (!row.canLink) return row.cannotLinkReason;
  if (changing.originalSageIsService != null && row.isService !== changing.originalSageIsService) {
    return changing.originalSageIsService
      ? 'This Sage item is a service, but this one keeps stock.'
      : 'This Sage item keeps stock, but this one is a service.';
  }
  return sameText(row.code, changing.code) ? `Same item code as ${changing.name}.` : null;
}

function linkedNote(row: SageLinkRow, type: FixMatchType): string | null {
  if (row.sageRecordId == null) return null;
  return row.originalSageName ? `Linked to Sage's ${row.originalSageName}` : `Has its own Sage ${fixMatchNoun(type)}, made by Aivor`;
}

export function pickerOptions(
  rows: SageLinkRow[],
  changing: SageLinkRow,
  type: FixMatchType,
  query: string,
  limit = PICKER_LIMIT
): { options: FixMatchOption[]; total: number } {
  const q = query.trim().toLowerCase();
  const matching = rows
    .filter((r) => r.aivorRecordId !== changing.aivorRecordId && (!q || contains(r.name, q) || contains(r.code, q)))
    .map((row) => ({ row, disabledReason: cannotPickReason(row, changing, type), linkedNote: linkedNote(row, type) }))
    .sort((a, b) => Number(!!a.disabledReason) - Number(!!b.disabledReason));
  return { options: matching.slice(0, limit), total: matching.length };
}

/** When the chosen record's own Sage record came from Sage, it is probably the changing one's match. */
export function swapOffered(changing: SageLinkRow, target: SageLinkRow | null, type: FixMatchType): boolean {
  if (!target || target.sageRecordId == null || target.originalSageName == null) return false;
  if (type === 'Supplier') return true;
  return changing.canLink && (target.originalSageIsService == null || target.originalSageIsService === changing.isService);
}

function quantity(value: number): string {
  return String(Number(value.toFixed(4)));
}

function previousFate(changing: SageLinkRow, type: FixMatchType): string {
  const noun = fixMatchNoun(type);
  if (type === 'Item') {
    if (changing.sendsAs === 'Bundle') return `${changing.name} still goes to Sage as a bundle.`;
    if (changing.itemTypeId === VARIABLE_ITEM_TYPE_ID) return `${changing.name} won't be sent to Sage, because variable items stay out of Sage.`;
    if (!changing.isActive) return `${changing.name} is inactive, so it won't be sent to Sage.`;
  }
  return `${changing.name} will be added to Sage as a new ${noun} on the next sync.`;
}

function givesBack(row: SageLinkRow, from: string): string | null {
  return row.stockFromLink && row.stockFromLink > 0
    ? `${row.name} gives back the ${quantity(row.stockFromLink)} it took from ${from} when they were linked, unless it has been used since.`
    : null;
}

export function whatWillChange(type: FixMatchType, changing: SageLinkRow, choice: FixMatchChoice): string[] {
  const items = type === 'Item';
  const sageName = changing.originalSageName ?? changing.name;
  const lines: (string | null)[] = [];

  if (choice.kind === 'move') {
    const { target, swap } = choice;
    lines.push(`Sage's ${sageName} will be linked to ${target.name}.`);
    lines.push(`On the next sync it takes ${target.name}'s ${items ? 'name and code' : 'name'}.`);
    lines.push(swap ? `${changing.name} will be linked to Sage's ${target.originalSageName}.` : previousFate(changing, type));
    if (items) lines.push(givesBack(changing, `Sage's ${sageName}`));

    if (target.sageRecordId != null && !swap) {
      lines.push(
        target.originalSageName
          ? `Sage's ${target.originalSageName} goes back to its own ${items ? 'name and code' : 'name'} and waits under Needs review.`
          : items
            ? `Sage's copy of ${target.name} will be renamed OLD-${target.sageRecordId} and made inactive. Any stock it holds stays on it in Sage.`
            : `Sage's copy of ${target.name} will be renamed "OLD COPY - ${target.name}" and made inactive.`
      );
    }
    if (items && target.sageRecordId != null) lines.push(givesBack(target, `its old Sage item`));
  } else {
    lines.push(`A new Aivor ${fixMatchNoun(type)}, ${items ? `${choice.code} ` : ''}${choice.name}, is created from Sage's record${items ? ", with Sage's current stock" : ''}.`);
    lines.push(`Sage's record takes the new ${items ? 'code and name' : 'name'} now.`);
    lines.push(previousFate(changing, type));
    if (items) lines.push(givesBack(changing, `Sage's ${sageName}`));
    if (items && choice.itemTypeId === FINISHED_GOOD_TYPE_ID && changing.originalSageIsService !== true) {
      lines.push('It needs a bill of materials before it goes to Sage.');
    }
  }

  lines.push('Nothing is deleted.');
  if (items) lines.push('Any stock difference shows under Stock corrections for you to approve.');
  return lines.filter((l): l is string => !!l);
}

export function moveToast(details: SageLinkMoveDetails): string {
  const held = details.releasedAs === 'OldCopy' && details.releasedSageQuantity ? details.releasedSageQuantity : 0;
  return held > 0
    ? `Link saved. The next sync will use it. The old copy in Sage still holds ${quantity(held)}. Write it off in Sage.`
    : 'Link saved. The next sync will use it.';
}
